/** @graphql cc **/

import { query } from '@confluence/query-preloader-tools';
import gql from 'graphql-tag';
import { getLogger } from '@confluence/logger';

import { fetchForgeAppsQuery, fetchForgeAppsQueryVariables } from './__types__/fetchForgeAppsQuery';
import { ExtensionEnvironment } from '@atlassian/forge-ui-types';
// eslint-disable-next-line import/no-cycle
import { ExtensionWithSimplifiedLicense } from './preloadUseExtensionList';

const logger = getLogger('fetchForgeApps');

export const fetchForgeApps = async ({
	cloudId,
	locale,
	types,
	context,
	includeHidden,
}: fetchForgeAppsQueryVariables): Promise<ExtensionWithSimplifiedLicense[] | null> => {
	const { data } = await query<fetchForgeAppsQuery, fetchForgeAppsQueryVariables>({
		query: gql`
			query fetchForgeAppsQuery(
				$cloudId: ID!
				$context: ConfluenceExtensionRenderingContextInput
				$includeHidden: Boolean
				$locale: String
				$types: [String]
			) {
				confluence_forgeExtensionsByType(
					cloudId: $cloudId
					context: $context
					includeHidden: $includeHidden
					locale: $locale
					types: $types
				) {
					id
					appId
					appVersion
					appOwner
					consentUrl
					environmentId
					environmentKey
					environmentType
					migrationKey
					key
					properties {
						key
						value
					}
					egress {
						addresses
						category
						type
						inScopeEUD
					}
					license {
						active
						capabilitySet
					}
					userAccess {
						enabled
						hasAccess
					}
					type
					installationId
					installationConfig {
						key
						value
					}
					hiddenBy
				}
			}
		`,
		variables: {
			cloudId,
			locale,
			types,
			context,
			includeHidden,
		},
	});

	return transformForgeApps(data);
};

const transformForgeApps = (
	data: fetchForgeAppsQuery | null,
): ExtensionWithSimplifiedLicense[] | null => {
	if (!data || !data.confluence_forgeExtensionsByType) {
		return null;
	}

	return data.confluence_forgeExtensionsByType.flatMap(
		(extension): ExtensionWithSimplifiedLicense[] => {
			if (!extension) {
				return [];
			}

			if (!extension.environmentKey) {
				logger.error`Invalid environment key for extension ${extension.id}: ${JSON.stringify(extension)}`;
				return [];
			}

			return [
				{
					id: extension.id,
					appId: extension.appId,
					key: extension.key,
					environmentId: extension.environmentId,
					environmentType:
						extension.environmentType === ExtensionEnvironment.DEVELOPMENT ||
						extension.environmentType === ExtensionEnvironment.STAGING ||
						extension.environmentType === ExtensionEnvironment.PRODUCTION
							? extension.environmentType
							: ExtensionEnvironment.DEVELOPMENT,
					environmentKey: extension.environmentKey,
					properties: extension.properties.reduce((acc, property) => {
						if (!property.value || !property.key) {
							logger.error`Invalid property for extension ${extension.id}: ${JSON.stringify(property)}`;
							return acc;
						}

						try {
							const jsonValue = JSON.parse(property.value);
							acc[property.key] = jsonValue;
						} catch (error) {
							acc[property.key] = property.value;
						}
						return acc;
					}, {}),
					license: extension.license
						? {
								active: extension.license.active,
								capabilitySet:
									extension.license.capabilitySet === 'CAPABILITY_ADVANCED'
										? 'capabilityAdvanced'
										: 'capabilityStandard',
							}
						: null,
					userAccess: extension.userAccess
						? {
								hasAccess: extension.userAccess.hasAccess ?? false,
								enabled: extension.userAccess.enabled ?? false,
							}
						: null,
					type: extension.type,
					installationId: extension.installationId,
					appVersion: extension.appVersion ?? undefined,
					consentUrl: extension.consentUrl ?? undefined,
					migrationKey: extension.migrationKey ?? undefined,
					egress:
						extension.egress?.flatMap((egress) => {
							if (!egress.type || !egress.addresses) {
								logger.error`Invalid egress for extension ${extension.id}: ${JSON.stringify(egress)}`;
								return [];
							}

							return [
								{
									type: egress.type,
									addresses: egress.addresses?.flatMap((address) => address ?? []),
									category: egress.category ?? undefined,
									inScopeEUD: egress.inScopeEUD ?? undefined,
								},
							];
						}) ?? undefined,
					installationConfig:
						extension.installationConfig?.flatMap((config) => {
							if (!config?.key || config.value === null) {
								logger.error`Invalid installation config for extension ${extension.id}: ${JSON.stringify(config)}`;
								return [];
							}

							return [
								{
									key: config.key,
									value: config.value,
								},
							];
						}) ?? undefined,
					dataClassificationPolicyDecision: {
						status: extension.hiddenBy ? 'BLOCKED' : 'ALLOWED',
					},
				},
			];
		},
	);
};
